import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { globalVars } from "../../global/GlobalVars.js";
import * as language from "../../utils/language.js";
import * as rwData from "../../utils/rwData.js";
import * as resolveType from "../../utils/resolveType.js";
import CustomKeyboard from "../System/Keyboard";

import "./Datafield.css";

const Datafield = observer((props) => {
  //========================================================================
  // Zustandsvariablen definieren
  const [inputVisibility, setInputVisibility] = useState(false);
  const [newValue, setNewValue] = useState();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const inputRef = useRef(null);

  //========================================================================
  // Wert aus globalVars auslesen
  let value = rwData.readRcvValue(props.props.variable);
  let resolvedValue = value.value;

  //========================================================================
  // Array auflösen
  if (props.element.index !== undefined) {
    resolvedValue = resolveType.readArray(value.value, props.element.index);
  }

  //========================================================================
  // Simulation
  let simulation = false;
  let simValue = 0;

  try {
    simulation = props.style.properties.simulation;
    simValue = props.style.properties.simValue;
  } catch (error) {
    simulation = false;
  }

  if (simulation) {
    resolvedValue = parseFloat(simValue);
  }

  //========================================================================
  // Einheit und Dezimalstellen des Wertes
  let displayValue;
  let type;
  let valueType = value.type ? value.type.toLowerCase() : "";

  if (
    valueType === "sint" ||
    valueType === "int" ||
    valueType === "dint" ||
    valueType === "lint" ||
    valueType === "usint" ||
    valueType === "uint" ||
    valueType === "udint" ||
    valueType === "ulint" ||
    valueType === "byte" ||
    valueType === "word" ||
    valueType === "dword" ||
    valueType === "lword"
  ) {
    type = "int";
  } else if (valueType.includes("array_int")) {
    type = "int";
  } else if (valueType.includes("array_uint")) {
    type = "int";
  } else if (valueType.includes("array_dint")) {
    type = "int";
  } else if (valueType.includes("array_real")) {
    type = "real";
  } else {
    type = valueType;
  }

  // kleine Helferfunktion fürs Null-Padding
  const pad = (num) => num.toString().padStart(2, "0");

  switch (type) {
    case "int":
      // Ganzzahl
      switch (value.unit) {
        case "hh:mm":
          {
            const hours = Math.floor(resolvedValue / 100);
            const minutes = resolvedValue % 100;
            displayValue = `${pad(hours)}:${pad(minutes)}`;
          }
          break;
        case "hh:mm:ss":
          {
            const hours = Math.floor(resolvedValue / 10000);
            const minutes = Math.floor((resolvedValue % 10000) / 100);
            const seconds = resolvedValue % 100;
            displayValue = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
          }
          break;
        case "datum":
          // Datum als Integer z.B. 13.01.2023 wäre 20230113
          let day = (resolvedValue % 100).toString().padStart(2, "0");
          let month = (Math.floor(resolvedValue / 100) % 100)
            .toString()
            .padStart(2, "0");
          let year = Math.floor(resolvedValue / 10000)
            .toString()
            .padStart(4, "0");
          displayValue = day + "." + month + "." + year;
          break;
        case "TOD":
          // Time of Day, Millisekunden seit Mitternacht, als Uhrzeit darstellen
          let hoursTod = Math.floor(resolvedValue / 3600000)
            .toString()
            .padStart(2, "0");
          let minutesTod = Math.floor((resolvedValue % 3600000) / 60000)

            .toString()
            .padStart(2, "0");
          let secondsTod = Math.floor((resolvedValue % 60000) / 1000)
            .toString()
            .padStart(2, "0");
          displayValue = hoursTod + ":" + minutesTod + ":" + secondsTod;
          break;
        case "DATE":
          // Date, Millisekunden seit 01.01.1970, als Datum darstellen
          let date = new Date(resolvedValue * 1000);
          let dayDate = date.getDate().toString().padStart(2, "0");
          let monthDate = (date.getMonth() + 1).toString().padStart(2, "0");
          let yearDate = date.getFullYear().toString().padStart(4, "0");
          displayValue = dayDate + "." + monthDate + "." + yearDate;
          break;
        default:
          // Zahl
          displayValue = (resolvedValue / 10 ** value.decimalPoints)
            .toFixed(value.decimalPoints)
            .toString(10)
            .concat(" ", value.unit)
            .replace(/\./g, ",");
      }
      break;
    case "real":
      // Kommazahl
      displayValue = (resolvedValue / 1)
        .toFixed(value.decimalPoints)
        .toString(10)
        .concat(" ", value.unit)
        .replace(/\./g, ",");
      break;
    case "string":
      // Text
      displayValue = resolvedValue;
      break;
    case "bool":
      displayValue = parseInt(resolvedValue) > 0 ? "true" : "false";
      break;
    default:
      displayValue = resolvedValue;
  }

  //========================================================================
  // neuen Wert schreiben
  const handleInputChange = () => {
    let formatedValue;
    let verifiedValue = null;
    if (newValue === undefined) return;

    switch (type) {
      case "int":
        // Ganzzahl
        switch (value.unit) {
          case "hh:mm":
            // Uhrzeit
            formatedValue = parseInt(
              newValue.split(":")[0] + newValue.split(":")[1],
              10,
            );
            verifiedValue = formatedValue;
            break;
          case "datum":
            // Datum
            formatedValue = parseInt(
              newValue.split(".")[0] +
                newValue.split(".")[1] +
                newValue.split(".")[2],
              10,
            );
            verifiedValue = formatedValue;
            break;
          default:
            // Zahl
            formatedValue = Math.round(
              newValue.replace(",", ".") * 10 ** value.decimalPoints,
            );
            if (isFinite(formatedValue)) {
              verifiedValue = formatedValue;
            }
        }
        break;
      case "real":
        // Kommazahl
        formatedValue = parseFloat(newValue.replace(",", "."));
        if (isFinite(formatedValue)) {
          verifiedValue = formatedValue;
        }
        break;
      case "string":
        // Text
        formatedValue = newValue;
        verifiedValue = formatedValue;
        break;
      case "bool":
        // Boolean
        verifiedValue = newValue > 0 ? 1 : 0;
        break;
      default:
    }

    let valueToWrite = verifiedValue;

    // Array schreiben
    if (props.element.index !== undefined) {
      let baseType;
      if (value.type.includes("ARRAY_INT")) {
        baseType = "INT";
      } else if (value.type.includes("ARRAY_UINT")) {
        baseType = "INT";
      } else if (value.type.includes("ARRAY_DINT")) {
        baseType = "INT";
      } else if (value.type.includes("ARRAY_REAL")) {
        baseType = "REAL";
      }

      valueToWrite = resolveType.writeArray(
        value.value,
        props.element.index,
        verifiedValue,
        baseType,
      );
    }

    // Wert in globalVars schreiben
    if (props.authWrite) {
      if (valueToWrite !== null) {
        rwData.writeValue(
          props.props.variable.toString(),
          valueToWrite.toString(),
          value.value,
        );
      }
    }
  };

  //========================================================================
  // Input-Element anzeigen und Output-Element verstecken
  const handleOutputClick = () => {
    if (value.setpoint && props.authWrite) {
      setInputVisibility(true);
    }
  };

  //========================================================================
  // Output-Element anzeigen und Input-Element verstecken
  const handleInputBlur = () => {};

  // bei Enter gleiches Verhalten wie bei onBlur
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setInputVisibility(false);
      handleInputChange();
      setNewValue("");
    }
  };

  const onEnter = () => {
    setKeyboardOpen(false);
    setInputVisibility(false);
    handleInputChange();
    setNewValue("");
  };

  //========================================================================
  // Tastatur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setKeyboardOpen(false);
        setInputVisibility(false);
        handleInputChange();
        setNewValue("");
      }
    };

    if (keyboardOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [keyboardOpen]);

  const url = window.location.href;
  const showKeyboard = url.includes("/keyboard");

  //========================================================================
  // Grafische Darstellung
  return (
    <div
      style={{
        border: "1px solid black",
        ...props.style,
        borderWidth: props.style.borderWidth || "1px",
        borderStyle: props.style.borderStyle || "solid",
        borderColor: props.style.borderColor || "black",
        visibility: props.props.visibility,
        cursor: "pointer",
        boxShadow:
          "inset 0px 0px 5px rgba(0,0,0,0.3), 2px 2px 5px rgba(0,0,0,0.3)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      title={
        props.element.tooltip
          ? language.displayText(props.element.tooltip) +
            (props.element.tooltip2
              ? "\n" + language.displayText(props.element.tooltip2)
              : "") +
            (props.element.tooltip3
              ? "\n" + language.displayText(props.element.tooltip3)
              : "")
          : language.displayText(value.name) +
            (props.element.tooltip2
              ? "\n" + language.displayText(props.element.tooltip2)
              : "") +
            (props.element.tooltip3
              ? "\n" + language.displayText(props.element.tooltip3)
              : "") +
            (globalVars.cyclicUpdate || globalVars.editor
              ? "\n" + ("Variable: " + props.props.variable)
              : "")
      }
    >
      {!inputVisibility && (
        <output
          onClick={handleOutputClick}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            paddingLeft: "3%",
            paddingRight: "3%",
            overflow: "hidden",
            whiteSpace: "nowrap",
            boxSizing: "border-box",
            justifyContent: props.element.textHorizontalAlignment,
            alignItems: props.element.textVerticalAlignment,
          }}
        >
          {displayValue}
        </output>
      )}
      {inputVisibility && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            width: "500px",
          }}
          ref={inputRef}
        >
          <input
            name="input"
            type="text"
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            value={newValue}
            onFocus={() => setKeyboardOpen(true)}
            onChange={(event) => setNewValue(event.target.value)}
            style={{
              fontSize: "inherit",
              height: "100%",
              width: "100%",
              backgroundColor: "transparent",
              outline: 0,
              borderColor: "transparent",
              textAlign: "center",
              color: "inherit",
              paddingLeft: "3%",
              paddingRight: "3%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              boxSizing: "border-box",
            }}
          />
          <CustomKeyboard
            type={type}
            newValue={newValue}
            setNewValue={setNewValue}
            onEnter={onEnter}
            showKeyboard={showKeyboard && keyboardOpen}
          />
        </div>
      )}
      <output
        style={{
          position: "absolute",
          backgroundColor: "grey",
          fontSize: "12px",
          width: "200%",
          height: "150%",
          whiteSpace: "pre-wrap",
          boxSizing: "border-box",
          justifyContent: props.element.textHorizontalAlignment,
          alignItems: props.element.textVerticalAlignment,
          visibility: "collapse",
        }}
      >
        {/* für Kontrolle, ob richtige Datenpunkte zugewiesen */}
        {value.name[0]}
        {value.id}
      </output>
    </div>
  );
});

export default Datafield;
